import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { updateAISettingsSchema, type UpdateAISettingsInput, type AIConfig } from '@octery/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AIPlayground } from './AIPlayground';
import { ConfigHistoryCard } from './ConfigHistoryCard';
import { LeadRolloutCard } from './LeadRolloutCard';
import { DialogCommandsCard } from './DialogCommandsCard';

export function AISettingsPage() {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => api.get<AIConfig>('/api/ai-settings'),
  });

  const mutation = useMutation({
    mutationFn: (data: UpdateAISettingsInput) =>
      api.patch<AIConfig>('/api/ai-settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
      toast.success('AI settings saved');
    },
    onError: () => {
      toast.error('Failed to save AI settings');
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<UpdateAISettingsInput>({
    resolver: zodResolver(updateAISettingsSchema),
    values: config
      ? {
          systemPrompt: config.systemPrompt,
          maxMessages: config.maxMessages,
          hardStopThreshold: config.hardStopThreshold,
          responseMaxLength: config.responseMaxLength,
          messagesContextAmount: config.messagesContextAmount,
        }
      : undefined,
  });

  const onSubmit = (data: UpdateAISettingsInput) => {
    mutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-1/4 animate-pulse" />
        <Card className="animate-pulse">
          <CardContent className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 bg-muted rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">AI Settings</h1>
        <p className="text-muted-foreground">Configure your AI agent behavior</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* System Prompt */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">System Prompt</CardTitle>
            <CardDescription>
              Instructions that define how your AI agent behaves
            </CardDescription>
          </CardHeader>
          <CardContent>
            <textarea
              className="w-full min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="You are a helpful assistant for..."
              {...register('systemPrompt')}
            />
            {errors.systemPrompt && (
              <p className="text-destructive text-xs mt-1">{errors.systemPrompt.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Conversation Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Conversation Limits</CardTitle>
            <CardDescription>
              Control conversation depth and behavior boundaries
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Max Messages</label>
              <Input type="number" {...register('maxMessages', { valueAsNumber: true })} />
              <p className="text-xs text-muted-foreground">
                Maximum number of messages before hard stop
              </p>
              {errors.maxMessages && (
                <p className="text-destructive text-xs">{errors.maxMessages.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Hard Stop Threshold</label>
              <Input type="number" {...register('hardStopThreshold', { valueAsNumber: true })} />
              <p className="text-xs text-muted-foreground">
                Non-meaningful messages before forced CTA
              </p>
              {errors.hardStopThreshold && (
                <p className="text-destructive text-xs">{errors.hardStopThreshold.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Max Response Length</label>
              <Input type="number" {...register('responseMaxLength', { valueAsNumber: true })} />
              <p className="text-xs text-muted-foreground">
                Maximum tokens in AI response
              </p>
              {errors.responseMaxLength && (
                <p className="text-destructive text-xs">{errors.responseMaxLength.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Context Window</label>
              <Input type="number" {...register('messagesContextAmount', { valueAsNumber: true })} />
              <p className="text-xs text-muted-foreground">
                Number of previous messages included as context
              </p>
              {errors.messagesContextAmount && (
                <p className="text-destructive text-xs">{errors.messagesContextAmount.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save */}
        <div className="flex justify-end gap-3">
          <Button type="submit" disabled={!isDirty || mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {mutation.isSuccess && (
          <p className="text-green-600 text-sm text-right">Settings saved successfully!</p>
        )}
        {mutation.isError && (
          <p className="text-destructive text-sm text-right">Failed to save settings</p>
        )}
      </form>

      {/* Test mode: lead rollout */}
      <LeadRolloutCard config={config} />

      {/* Dialog commands: greeting and test window */}
      <DialogCommandsCard config={config} />

      {/* AI Playground */}
      <AIPlayground />

      {/* Config History */}
      <ConfigHistoryCard />
    </div>
  );
}
